import api from "../api/client";

function newEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function fbq(...args) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq(...args);
  }
}

/**
 * Fire a Meta Pixel event in the browser AND mirror it server-side via CAPI.
 * Both fires share the same eventId so Meta deduplicates them.
 *
 * `userData` (optional) is hashed server-side; never pass raw PII to the
 * browser pixel itself.
 */
export function pixelEvent(eventName, { customData, userData, eventId } = {}) {
  const id = eventId || newEventId();

  // Browser fire — third arg supports eventID for dedup with CAPI.
  fbq("track", eventName, customData || {}, { eventID: id });

  // Mirror to backend (fire-and-forget, never blocks UI)
  api
    .post("/api/meta/track", {
      eventName,
      eventId: id,
      customData: customData || {},
      user: userData || {},
      eventSourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
    })
    .catch(() => {});

  return id;
}

/** Pre-built helpers — keep the payload shape consistent across the app. */
export function pixelPageView() {
  pixelEvent("PageView");
}

export function pixelViewContent(product) {
  if (!product) return;
  const price = product.salePrice || product.price;
  pixelEvent("ViewContent", {
    customData: {
      content_type: "product",
      content_ids: [String(product.id)],
      content_name: product.name,
      content_category: product.category,
      currency: "RSD",
      value: Number(price) || 0,
    },
  });
}

export function pixelAddToCart(product, size, quantity = 1) {
  if (!product) return;
  const price = product.salePrice || product.price;
  pixelEvent("AddToCart", {
    customData: {
      content_type: "product",
      content_ids: [String(product.id)],
      content_name: product.name,
      content_category: product.category,
      contents: [
        {
          id: String(product.id),
          quantity,
          item_price: Number(price) || 0,
        },
      ],
      currency: "RSD",
      value: (Number(price) || 0) * quantity,
      ...(size ? { variant: String(size) } : {}),
    },
  });
}

export function pixelInitiateCheckout({ items, total }) {
  const list = Array.isArray(items) ? items : [];
  pixelEvent("InitiateCheckout", {
    customData: {
      content_type: "product",
      content_ids: list.map((i) => String(i.id || i.productId)).filter(Boolean),
      contents: list.map((i) => ({
        id: String(i.id || i.productId),
        quantity: Number(i.quantity) || 1,
        item_price: Number(i.price) || 0,
      })),
      num_items: list.reduce((n, i) => n + (Number(i.quantity) || 1), 0),
      currency: "RSD",
      value: Number(total) || 0,
    },
  });
}

/**
 * Purchase uses a deterministic event_id built from orderNumber so the
 * browser and server fires (CAPI from orders/payment controllers) dedupe.
 */
export function pixelPurchase(order) {
  if (!order || !order.orderNumber) return;
  const list = Array.isArray(order.items) ? order.items : [];
  pixelEvent("Purchase", {
    eventId: `purchase_${order.orderNumber}`,
    customData: {
      content_type: "product",
      content_ids: list.map((i) => String(i.id || i.productId)).filter(Boolean),
      contents: list.map((i) => ({
        id: String(i.id || i.productId),
        quantity: Number(i.quantity) || 1,
        item_price: Number(i.price) || 0,
      })),
      num_items: list.reduce((n, i) => n + (Number(i.quantity) || 1), 0),
      currency: "RSD",
      value: Number(order.total) || 0,
      order_id: order.orderNumber,
    },
  });
}

/**
 * Lead — returns the eventId so callers can forward it to the backend
 * (newsletter subscribe) for deduplication with the server-side CAPI fire.
 */
export function pixelLead({ email, name, source } = {}) {
  return pixelEvent("Lead", {
    customData: {
      content_name: source || "newsletter_signup",
    },
    userData: {
      ...(email ? { email } : {}),
      ...(name ? { firstName: name } : {}),
    },
  });
}
